import { Constructor } from '@angular/cdk/table';
import { Injector } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Subject } from 'rxjs';
import { IArticleContentDto } from 'src/app/core/dtos/interfaces/i-article-content-dto';
import { ContentEditorComponent } from 'src/app/shared-form-sections/components/content-editor/content-editor.component';
import { HeadlineInputComponent } from 'src/app/shared-form-sections/components/headline-input/headline-input.component';
import { StandfirstInputComponent } from 'src/app/shared-form-sections/components/standfirst-text/standfirst-text.component';
import { IContentConflictForm } from 'src/app/shared-form-sections/interfaces/i-content-conflict-form';
import { CONTROL, BODY_CONTROL } from '../injectors/control-injector';
import { languageNameByCode } from '../utils/language-name-by-code';
import { siteNameByCode } from '../utils/site-name-by-code';
import { ILanguage } from 'src/app/core/models/i-language';
import { IDeliveryTarget } from 'src/app/core/models/i-delivery-target';

type WithConflictsType = {
  _initChangedContentVersion(
    articleContentDto: IArticleContentDto,
    allLanguages: ILanguage[],
    deliveryTargets: IDeliveryTarget[]
  );
  _initInitialContentVersion(
    articleContentDto: IArticleContentDto,
    allLanguages: ILanguage[],
    deliveryTargets: IDeliveryTarget[]
  );
};

type ConflictsRequerimentsBase = {
  destroy$: Subject<boolean>;
  isInitialContentMergingMode: boolean;
  isChangedContentMergingMode: boolean;
  initialContentToCompare: any[];
  changedContentToCompare: any[];
  changedArticleContent: IArticleContentDto;
  initialArticleContent: IArticleContentDto;
  initialContentForm: FormGroup<IContentConflictForm>;
  changedContentForm: FormGroup<IContentConflictForm>;
  parentInjector: Injector;
};

export function WithConflictsSection<
  TBase extends Constructor<ConflictsRequerimentsBase>
>(
  baseClass: TBase
): {
  new (...args: any[]): WithConflictsType;
  prototype: WithConflictsType;
} & TBase {
  return class WithConflictsSectionable extends baseClass {
    constructor(...args: any[]) {
      super(...args);
    }
    _initChangedContentVersion(
      articleContentDto: IArticleContentDto,
      allLanguages: ILanguage[],
      deliveryTargets: IDeliveryTarget[]
    ) {
      this.changedArticleContent = articleContentDto;
      this.changedContentForm = this.createForm(
        this.changedArticleContent,
        this.isChangedContentMergingMode
      );
      
      this.changedContentToCompare = this.componentsToCompare(
        this.changedContentForm.controls.headline,
        this.changedContentForm.controls.standfirst,
        languageNameByCode(
          allLanguages,
          this.changedArticleContent.languageCode
        ),
        siteNameByCode(
          deliveryTargets,
          this.changedArticleContent.deliveryTargetCodes
        ),
        this.changedArticleContent.authors
          .map((author) => author.name)
          .join(', '),
        this.changedContentForm.controls.body
      );
    }
    _initInitialContentVersion(
      articleContentDto: IArticleContentDto,
      allLanguages: ILanguage[],
      deliveryTargets: IDeliveryTarget[]
    ) {
      this.initialArticleContent = articleContentDto;
      this.initialContentForm = this.createForm(
        this.initialArticleContent,
        this.isInitialContentMergingMode
      );

      this.initialContentToCompare = this.componentsToCompare(
        this.initialContentForm.controls.headline,
        this.initialContentForm.controls.standfirst,
        languageNameByCode(
          allLanguages,
          this.initialArticleContent.languageCode
        ),
        siteNameByCode(
          deliveryTargets,
          this.initialArticleContent.deliveryTargetCodes
        ),
        this.initialArticleContent.authors
          .map((author) => author.name)
          .join(', '),
        this.initialContentForm.controls.body
      );
    }
    private createForm(
      content: IArticleContentDto,
      isEditMode: boolean
    ): FormGroup<IContentConflictForm> {
      return new FormGroup({
        headline: new FormControl<string>(content.headline, [
          Validators.required,
        ]),
        standfirst: new FormControl<string>(content.standfirst),
        body: new FormControl<string>({
          value: content.body,
          disabled: !isEditMode,
        }),
      });
    }

    private componentsToCompare(
      headline: FormControl<string>,
      standfirst: FormControl<string>,
      language: string,
      websites: string,
      authors: string,
      body: FormControl<string>
    ) {
      return [
        {
          title: 'Headline',
          text: headline,
          component: HeadlineInputComponent,
          injector: Injector.create(
            [
              {
                provide: CONTROL,
                useValue: headline,
              },
            ],
            this.parentInjector
          ),
        },
        {
          title: 'Standfirst',
          text: standfirst,
          component: StandfirstInputComponent,
          injector: Injector.create(
            [
              {
                provide: CONTROL,
                useValue: standfirst,
              },
            ],
            this.parentInjector
          ),
        },
        {
          title: 'Content Body',
          text: body,
          component: ContentEditorComponent,
          injector: Injector.create(
            [
              {
                provide: BODY_CONTROL,
                useValue: body,
              },
            ],
            this.parentInjector
          ),
          isStandalone: true,
        },
        {
          title: 'Language',
          text: language,
        },
        {
          title: 'Publishing Destinations',
          text: websites,
        },
        {
          title: 'Authors',
          text: authors,
        },
      ];
    }
  };
}
